import * as React from 'react';
import { useState } from 'react';

import { darken, lighten } from 'polished';
import styled from 'styled-components';

import { COLOR, RADIUS } from '../../packages/constants';
import { CircleWithX, Search } from '../../packages/icons';

export interface SearchFilterProps {
    assistAccess: boolean;
    handleChange?: (e: any) => void;
    handleClearButton?: (e: any) => void;
    placeholder?: string;
    searchTerm?: string;
    ref?: any;
    isExpandable?: boolean;
    isRounded?: boolean;
    dataId?: string;
    hasFocus?: boolean;
    hasClearButton?: boolean;
    hasSearchIcon?: boolean;
}

interface StyledInputProps {
    assistAccess: boolean;
    dataTestId: string;
    autoComplete?: string;
    hideExpandButton?: boolean;
    isRounded?: boolean;
    isExpandable?: boolean;
    hasEndContent: boolean;
    ref?: any;
    rel?: string;
}

interface DataTestIdProps {
    dataTestId: string;
}

interface ExpandButtonProps {
    isRounded?: boolean;
    hideExpandButton?: boolean;
}

interface StyledFilterProps {
    isExpanded: boolean;
    isRounded?: boolean;
}

const StyledFilter = styled.div<StyledFilterProps>`
    display: flex;
    min-height: 34px;
    border: ${(props) => (props.isExpanded ? `1px solid ${COLOR.MAIN.FORM_FIELD}` : 'none')};
    border-radius: ${(props): string => (props.isRounded ? '25px' : RADIUS.FORM_FIELD)};

    &:focus-within {
        border-color: ${COLOR.BRAND.REPORTING};
    }
`;

const StyledCircleWithX = styled(CircleWithX)`
    width: 18px;
    height: 22px;
`;

const StyledSearch = styled(Search)`
    width: 18px;
    height: 22px;
    fill: #9ba8bd;
`;

const Button = styled.button.attrs<DataTestIdProps>(({ dataTestId }) => ({
    'data-test-id': dataTestId,
}))``;

interface StyledButtonProps {
    isRounded?: boolean;
    isExpandable?: boolean;
    assistAccess?: boolean;
    hideExpandButton?: boolean;
}

const StyledButton = styled(Button)<StyledButtonProps>`
    fill: ${COLOR.MAIN.FORM_FIELD_DARK};
    background-color: ${COLOR.PRIMARY.WHITE};
    border-radius: ${(props): string =>
        props.isRounded ? '0 25px 25px 0' : `0 ${RADIUS.FORM_FIELD} ${RADIUS.FORM_FIELD} 0`};
    border: none;
    padding: 6px;
    cursor: pointer;
    display: ${(props) => (!props.isExpandable ? 'block' : props.hideExpandButton ? 'block' : 'none')};

    &:focus {
        outline: ${(props): string => (props.assistAccess ? '1px dotted #212121' : '0')};
        outline: ${(props): string => (props.assistAccess ? '5px auto -webkit-focus-ring-color' : '0')};

        ${StyledCircleWithX} {
            fill: ${COLOR.BRAND.REPORTING};
        }
    }

    &:hover {
        ${StyledCircleWithX} {
            fill: ${darken('0.2', COLOR.MAIN.FORM_FIELD_DARK)};
        }
    }

    &:disabled {
        cursor: default;

        ${StyledCircleWithX} {
            opacity: 0.15;
        }
    }
`;

interface StyledSearchIconContainerProps {
    isRounded?: boolean;
}

const StyledSearchIconContainer = styled.div<StyledSearchIconContainerProps>`
    fill: ${COLOR.MAIN.FORM_FIELD_DARK};
    background-color: ${COLOR.PRIMARY.WHITE};
    border-radius: ${(props): string =>
        props.isRounded ? '0 25px 25px 0' : `0 ${RADIUS.FORM_FIELD} ${RADIUS.FORM_FIELD} 0`};
    padding: 6px;
    display: flex;
    align-items: center;
`;

const TextInput = styled.input.attrs<DataTestIdProps>(({ dataTestId }) => ({
    'data-test-id': dataTestId,
}))``;

const StyledInput = styled(TextInput)<StyledInputProps>`
    font-size: 14px;
    color: ${darken('0.2', COLOR.MAIN.FORM_FIELD_DARK)};
    border-radius: ${(props) => (props.isRounded ? '25px' : RADIUS.FORM_FIELD)};

    ${(props) =>
        props.hasEndContent &&
        `
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
    `};

    border: none;
    padding: ${(props) => (!props.isExpandable ? '2px 6px' : props.hideExpandButton ? '2px 6px' : 0)};
    width: ${(props) => (props.isExpandable ? (props.hideExpandButton ? '100%' : '0') : '100%')};
    background-color: ${(props) =>
        props.isExpandable ? (props.hideExpandButton ? COLOR.PRIMARY.WHITE : 'transparent') : COLOR.PRIMARY.WHITE};

    &::placeholder {
        opacity: ${(props) => (!props.isExpandable ? 1 : props.hideExpandButton ? 1 : 0)};
        color: ${lighten('0.2', COLOR.MAIN.FORM_FIELD_DARK)};
    }

    &:focus {
        border-color: ${COLOR.BRAND.REPORTING};
        outline: ${(props: StyledInputProps): string => (props.assistAccess ? '1px dotted #212121' : '0')};
        outline: ${(props: StyledInputProps): string =>
            props.assistAccess ? '5px auto -webkit-focus-ring-color' : '0'};

        ~ ${StyledButton} {
            border-color: ${COLOR.BRAND.REPORTING};
        }
        ~ ${StyledSearchIconContainer} {
            border-color: ${COLOR.BRAND.REPORTING};
        }
    }

    /* 'fix' for removing the internal clear button on Microsoft Edge */
    &::-ms-clear {
        display: none;
    }
`;

const ExpandButton = styled.button<ExpandButtonProps>`
    font-size: 14px;
    color: ${darken('0.2', COLOR.MAIN.FORM_FIELD_DARK)};
    cursor: pointer;
    border: 1px solid ${darken(0.04, COLOR.MAIN.MEDIUM_GREY)};
    background-color: ${COLOR.PRIMARY.WHITE};
    border-radius: ${(props) => (props.isRounded ? '25px' : `${RADIUS.FORM_FIELD}`)};
    padding: 2px 8px;
    min-height: 36px;
    display: ${({ hideExpandButton }) => (hideExpandButton ? 'none' : 'flex')};
    align-items: center;
    justify-content: center;

    &:hover {
        border-color: ${lighten(0.5, COLOR.MAIN.LABEL_TEXT)};
        background-color: ${lighten(0.65, COLOR.MAIN.LABEL_TEXT)};

        ${StyledSearch} {
            fill: ${darken('0.2', COLOR.MAIN.FORM_FIELD_DARK)};
        }
    }
`;

const SearchFilter = React.forwardRef<HTMLInputElement, SearchFilterProps>(
    (
        {
            assistAccess,
            placeholder,
            handleChange,
            handleClearButton,
            searchTerm,
            isRounded,
            isExpandable,
            dataId,
            hasFocus,
            hasClearButton = true,
            hasSearchIcon = false,
            ...props
        },
        ref,
    ) => {
        const [hideExpandButton, setHideExpandButton] = useState(false);

        // If the input has a search icon, only show the clear button if a search term has been entered
        // Otherwise, show the search icon
        const isExpanded = !isExpandable || hideExpandButton;
        const showClearButton = hasClearButton && (!hasSearchIcon || !!searchTerm);
        const showSearchIcon = isExpanded && hasSearchIcon && !showClearButton;

        const handleExpandButton = () => {
            setHideExpandButton(true);
            // @ts-ignore
            ref.current.focus();
        };

        const handleBlur = () => {
            if (!isExpandable) return;
            // @ts-ignore
            if (!ref.current.value) setHideExpandButton(false);
        };

        return (
            <StyledFilter isExpanded={isExpanded} isRounded={isRounded} {...props}>
                {isExpandable && (
                    <ExpandButton
                        isRounded={isRounded}
                        onClick={handleExpandButton}
                        hideExpandButton={hideExpandButton}
                    >
                        <StyledSearch />
                    </ExpandButton>
                )}
                <StyledInput
                    assistAccess={assistAccess}
                    type={'text'}
                    placeholder={placeholder}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    ref={ref}
                    rel={'search'}
                    aria-hidden={isExpandable && !hideExpandButton}
                    tabIndex={isExpandable ? (hideExpandButton ? 0 : -1) : 0}
                    autoComplete={'off'}
                    value={searchTerm}
                    dataTestId={dataId ? `${dataId}__filter-input` : 'filter__input'}
                    isRounded={isRounded}
                    isExpandable={isExpandable}
                    hideExpandButton={hideExpandButton}
                    autoFocus={hasFocus}
                    hasEndContent={hasClearButton || hasSearchIcon}
                />
                {showClearButton && (
                    <StyledButton
                        assistAccess={assistAccess}
                        type={'button'}
                        title={'Clear'}
                        onClick={handleClearButton}
                        disabled={!searchTerm}
                        data-test-id={dataId ? `${dataId}__filter-button--clear` : 'filter__button--clear'}
                        isRounded={isRounded}
                        isExpandable={isExpandable}
                        hideExpandButton={hideExpandButton}
                    >
                        <StyledCircleWithX />
                    </StyledButton>
                )}
                {showSearchIcon && (
                    <StyledSearchIconContainer
                        isRounded={isRounded}
                        data-test-id={dataId ? `${dataId}__filter-icon-search` : 'filter__icon-search'}
                    >
                        <StyledSearch />
                    </StyledSearchIconContainer>
                )}
            </StyledFilter>
        );
    },
);
SearchFilter.displayName = 'SearchFilter';

export { SearchFilter };
